import React, { useState, useEffect } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DashboardGpsService } from '../../services/DashboardGps/DashboardGpsService';
import MapaCalorGps from './MapaCalorGps';

const service = new DashboardGpsService();

const COL = { Candado: '#E08A08', Vehicular: '#3B82C4' };
const opts = (arr) => (arr || []).map((v) => ({ label: v, value: v }));
const num = (n) => (n == null ? 0 : Number(n)).toLocaleString('es-EC');
// Las fechas llegan como epoch (ms) o ISO; normaliza a Date.
const toDate = (v) => {
    if (v == null) return null;
    if (v instanceof Date) return v;
    if (typeof v === 'number') return new Date(v);
    if (/^\d+$/.test(String(v))) return new Date(Number(v));
    const d = new Date(String(v).replace(' ', 'T'));
    return isNaN(d.getTime()) ? null : d;
};
const fmtFecha = (v) => { const d = toDate(v); return d ? d.toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : ''; };
const p2 = (n) => String(n).padStart(2, '0');
const fmtFechaFull = (v) => { const d = toDate(v); return d ? `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}` : ''; };
const fmtHora = (v) => { const d = toDate(v); return d ? d.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''; };
const fechaProc = (f) => fmtFecha(f);

const DashboardAlarmasGps = () => {
    const [cortes, setCortes] = useState([]);
    const [corteId, setCorteId] = useState(null);
    const [data, setData] = useState(null);
    const [grupos, setGrupos] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [rutas, setRutas] = useState([]);
    const [placas, setPlacas] = useState([]);
    const [segmento, setSegmento] = useState('todos'); // todos | candado | flota
    const familias = segmento === 'candado' ? ['Candado'] : segmento === 'flota' ? ['Vehicular'] : [];
    const [dia, setDia] = useState(null);
    const [foco, setFoco] = useState(null);
    const [sectorSel, setSectorSel] = useState(null); // geohash del sector cuyos tramos se muestran
    const [recorrido, setRecorrido] = useState(null); // traza + alarmas del vehiculo seleccionado
    const [cargandoRec, setCargandoRec] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [sectorNombres, setSectorNombres] = useState({}); // geohash -> nombre de lugar
    const [loading, setLoading] = useState(false);
    const [gen, setGen] = useState(false);
    const [genC, setGenC] = useState(false);
    const [rango, setRango] = useState(null); // [desde, hasta] para corte completo

    // Centra el mapa en un punto (y dibuja la zona sin señal aprox) y hace scroll hasta el mapa.
    const irAlMapa = (lat, lon, label, minutos) => {
        if (lat == null || lon == null) return;
        setSectorSel(null);
        setFoco({ lat, lon, label, minutos });
        const el = document.getElementById('dashgps-mapa');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Muestra en el mapa TODOS los tramos individuales de un sector (geohash).
    const verSector = (gh, lat, lon) => {
        setSectorSel(gh);
        setFoco(lat != null ? { lat, lon, label: gh } : null);
        const el = document.getElementById('dashgps-mapa');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    const limpiarFoco = () => { setFoco(null); setSectorSel(null); setRecorrido(null); };

    // Carga el recorrido real (traza) del vehiculo seleccionado (1 sola placa).
    const verRecorrido = async () => {
        if (placas.length !== 1) return;
        setCargandoRec(true);
        setSectorSel(null); setFoco(null);
        try {
            const r = await service.trayectoria(corteId, placas[0]);
            setRecorrido(r);
            const el = document.getElementById('dashgps-mapa');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } finally { setCargandoRec(false); }
    };

    useEffect(() => {
        service.cortes().then((c) => { setCortes(c || []); if (c && c.length) setCorteId(c[0].corte_id); });
    }, []);

    useEffect(() => {
        if (!corteId) return;
        setLoading(true);
        setRecorrido(null); // la traza depende del filtro; se recarga a pedido
        service.dashboard(corteId, grupos, empresas, tipos, dia, rutas, placas, familias)
            .then((d) => setData(d))
            .finally(() => setLoading(false));
    }, [corteId, grupos, empresas, tipos, dia, rutas, placas, segmento]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reverse-geocode de los sectores (geohash -> nombre de lugar) con el propio Google Maps.
    useEffect(() => {
        if (!mapReady || !window.google || !window.google.maps) return;
        const pend = {};
        const add = (gh, lat, lon) => { if (gh && lat != null && lon != null && !(gh in sectorNombres) && !(gh in pend)) pend[gh] = { lat, lon }; };
        ((data && data.zonasOscuras) || []).forEach((z) => add(z.sector_nombre, z.lat, z.lon));
        ((data && data.riesgoContaminacion) || []).forEach((z) => add(z.sector_nombre, z.latitud, z.longitud));
        const keys = Object.keys(pend);
        if (!keys.length) return;
        const geocoder = new window.google.maps.Geocoder();
        const shortName = (r) => {
            const c = (t) => { const x = r.address_components.find((a) => a.types.includes(t)); return x ? x.long_name : null; };
            return c('locality') || c('sublocality') || c('administrative_area_level_2') || c('administrative_area_level_1') || (r.formatted_address || '').split(',')[0];
        };
        keys.forEach((gh) => {
            geocoder.geocode({ location: { lat: Number(pend[gh].lat), lng: Number(pend[gh].lon) } }, (res, status) => {
                const nombre = (status === 'OK' && res && res.length) ? shortName(res[0]) : null;
                setSectorNombres((prev) => ({ ...prev, [gh]: nombre || gh }));
            });
        });
    }, [data, mapReady]); // eslint-disable-line react-hooks/exhaustive-deps

    const generarMuestra = async () => {
        setGen(true);
        try {
            await service.generarCorte('muestra');
            const cs = await service.cortes();
            setCortes(cs || []);
            if (cs && cs.length) setCorteId(cs[0].corte_id);
        } finally { setGen(false); }
    };

    // Corte COMPLETO por rango (mes). Escribe en prod; puede tardar varios minutos.
    const ymd = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null);
    const generarCompleto = async () => {
        const d = rango && rango[0], h = rango && rango[1];
        if (!d || !h) { alert('Elegí el rango (desde / hasta) del corte.'); return; }
        const hInc = new Date(h.getTime() + 24 * 3600 * 1000); // hasta inclusivo (incluye el último día)
        setGenC(true);
        try {
            await service.generarCorte('completo', ymd(d), ymd(hInc));
            const cs = await service.cortes();
            setCortes(cs || []);
            if (cs && cs.length) setCorteId(cs[0].corte_id);
        } finally { setGenC(false); }
    };

    const corteSel = (cortes || []).find((c) => c.corte_id === corteId);
    const k = (data && data.kpis) || {};
    const porTipo = (data && data.porTipo) || [];
    const porEmpresa = (data && data.porEmpresa) || [];
    const porVehiculo = (data && data.porVehiculo) || [];
    const porRuta = (data && data.porRuta) || [];
    const porDia = (data && data.porDia) || [];
    const zonas = (data && data.zonasOscuras) || [];
    const zonasTramos = (data && data.zonasTramos) || [];
    const mapaCalor = (data && data.mapaCalor) || [];
    const riesgos = (data && data.riesgoContaminacion) || [];
    const tramosSel = sectorSel ? zonasTramos.filter((t) => t.sector_nombre === sectorSel) : null;
    const maxRuta = Math.max(1, ...porRuta.map((x) => Number(x.n)));
    const nombreSector = (gh) => sectorNombres[gh] || gh;
    // Intervalo de perdida de senal: prefiere fecha_fin; si falta, inicio + minutos.
    const finZona = (r) => toDate(r.fecha_fin) || (toDate(r.fecha_ini) ? new Date(toDate(r.fecha_ini).getTime() + (Number(r.minutos_sin_senal) || 0) * 60000) : null);

    const maxTipo = Math.max(1, ...porTipo.map((x) => Number(x.n)));
    const maxEmp = Math.max(1, ...porEmpresa.map((x) => Number(x.n)));

    // por dia -> pivot {dia: {Candado, Vehicular, total}}
    const dias = {};
    porDia.forEach((r) => {
        const d = r.dia; if (!dias[d]) dias[d] = { Candado: 0, Vehicular: 0, total: 0 };
        dias[d][r.familia] = (dias[d][r.familia] || 0) + Number(r.n); dias[d].total += Number(r.n);
    });
    const diasArr = Object.keys(dias).map(Number).sort((a, b) => a - b);
    const maxDia = Math.max(1, ...diasArr.map((d) => dias[d].total));

    const card = { background: '#fff', border: '1px solid #DBE3EC', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(16,32,43,.06)' };
    const toggle = (arr, setter, v) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    const bar = (label, n, max, color, onClick, active) => (
        <div key={label} onClick={onClick} title={onClick ? 'Clic para filtrar todo' : ''}
            style={{ display: 'grid', gridTemplateColumns: '190px 1fr 60px', gap: 10, alignItems: 'center', padding: '3px 6px', borderRadius: 6, cursor: onClick ? 'pointer' : 'default', background: active ? '#E1F3F1' : 'transparent' }}>
            <span style={{ fontSize: 12.5, color: active ? '#0B7E7A' : '#566575', fontWeight: active ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
            <div style={{ height: 9, background: '#F4F7FA', borderRadius: 5 }}>
                <div style={{ height: '100%', width: `${(n / max * 100).toFixed(0)}%`, background: color, borderRadius: 5 }} />
            </div>
            <span style={{ fontSize: 12.5, textAlign: 'right', fontFamily: 'monospace' }}>{num(n)}</span>
        </div>
    );

    return (
        <div style={{ padding: '10px 4px', color: '#16202B' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>🛰️ Dashboard Gerencial — Alarmas GPS</h2>
                <span style={{ background: '#EDE7FB', color: '#7C56E6', fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999 }}>Solo rol Dashboard Alarmas GPS</span>
                <div style={{ flex: 1 }} />
                <Calendar value={rango} onChange={(e) => setRango(e.value)} selectionMode="range" readOnlyInput
                    dateFormat="dd/mm/yy" placeholder="Rango (desde – hasta)" showButtonBar style={{ width: 220 }} />
                <Button label="Generar corte completo" icon="pi pi-calendar" loading={genC} onClick={generarCompleto} className="p-button-sm" title="Procesa el rango elegido (puede tardar varios minutos)" />
                <Button label="Muestra (3h)" icon="pi pi-bolt" loading={gen} onClick={generarMuestra} className="p-button-sm p-button-outlined" title="Corte rápido de 3h para validar" />
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
                <div><div style={lbl}>Período (corte)</div>
                    <Dropdown value={corteId} options={(cortes || []).map((c) => ({ label: `#${c.corte_id} · ${fmtFechaFull(c.fecha_desde)} → ${fmtFechaFull(c.fecha_hasta)} · ${c.modo} · ${num(c.total_eventos)} ev`, value: c.corte_id }))}
                        onChange={(e) => setCorteId(e.value)} placeholder="Sin cortes" style={{ minWidth: 420 }} /></div>
                <div><div style={lbl}>Segmento</div>
                    <SelectButton value={segmento} onChange={(e) => e.value && setSegmento(e.value)}
                        options={[{ label: 'Todos', value: 'todos' }, { label: '🔒 Candados', value: 'candado' }, { label: '🚚 Flota', value: 'flota' }]} /></div>
                <div><div style={lbl}>Grupo</div>
                    <MultiSelect value={grupos} options={opts(data && data.optGrupos)} onChange={(e) => setGrupos(e.value)} filter placeholder="Todos" display="chip" style={{ minWidth: 180, maxWidth: 260 }} /></div>
                <div><div style={lbl}>Empresa</div>
                    <MultiSelect value={empresas} options={opts(data && data.optEmpresas)} onChange={(e) => setEmpresas(e.value)} filter placeholder="Todas" display="chip" style={{ minWidth: 180, maxWidth: 260 }} /></div>
                <div><div style={lbl}>Tipo de alarma</div>
                    <MultiSelect value={tipos} options={opts(data && data.optEventos)} onChange={(e) => setTipos(e.value)} filter placeholder="Todos" display="chip" style={{ minWidth: 180, maxWidth: 300 }} /></div>
                {loading && <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, color: '#0E9E97', marginBottom: 8 }} />}
            </div>

            <div style={{ margin: '0 0 14px', fontSize: 12, color: (grupos.length || empresas.length || tipos.length || rutas.length || placas.length || dia || segmento !== 'todos') ? '#0B7E7A' : '#8494A3' }}>
                {(grupos.length || empresas.length || tipos.length || rutas.length || placas.length || dia || segmento !== 'todos')
                    ? <>Filtros activos{segmento !== 'todos' ? ` · ${segmento === 'candado' ? 'Candados' : 'Flota'}` : ''}{dia ? ` · día ${dia}` : ''}{rutas.length ? ` · ${rutas.length} ruta(s)` : ''}{placas.length ? ` · ${placas.length} placa(s)` : ''} → todos los gráficos recalculan. <span onClick={() => { setGrupos([]); setEmpresas([]); setTipos([]); setRutas([]); setPlacas([]); setDia(null); setSegmento('todos'); }} style={{ color: '#E23D3D', cursor: 'pointer', fontWeight: 600 }}>✕ Limpiar</span></>
                    : <>💡 Clic en un <b>tipo</b>, <b>empresa</b>, <b>día</b>, <b>ruta</b> o <b>vehículo</b> (o los combos de arriba) para filtrar <b>todo</b> el tablero.</>}
            </div>

            {corteSel && (
                <div style={{ margin: '0 0 12px', fontSize: 11.5, color: '#8494A3' }}>
                    📅 Corte #{corteSel.corte_id} · <b>{corteSel.modo}</b> · ventana {fmtFechaFull(corteSel.fecha_desde)} → {fmtFechaFull(corteSel.fecha_hasta)}
                    {corteSel.duracion_seg != null && <> · procesado en <b>{corteSel.duracion_seg < 90 ? `${corteSel.duracion_seg}s` : `${Math.round(corteSel.duracion_seg / 60)} min`}</b></>}
                    {corteSel.total_zonas_osc != null && <> · {num(corteSel.total_zonas_osc)} zonas oscuras</>}
                    · procesado {fechaProc(corteSel.fecha_proceso)}
                </div>
            )}

            {!corteId && <div style={{ ...card, textAlign: 'center', color: '#8494A3' }}>No hay cortes procesados. Generá un corte de muestra para ver datos.</div>}

            {corteId && <>
                {/* KPIs */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 14 }}>
                    {[['Eventos totales', k.total, '#0E9E97'], ['Críticos', k.criticos, '#E23D3D'], ['Familia Candado', k.candado, '#E08A08'], ['Familia Vehicular', k.vehicular, '#3B82C4'], ['En zona oscura', k.en_zona_oscura, '#7C56E6']].map((x) => (
                        <div key={x[0]} style={{ ...card, borderLeft: `3px solid ${x[2]}` }}>
                            <div style={{ fontSize: 11, color: '#566575', fontWeight: 600 }}>{x[0]}</div>
                            <div style={{ fontSize: 24, fontWeight: 680, fontFamily: 'monospace' }}>{num(x[1])}</div>
                        </div>
                    ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                    {/* Por tipo */}
                    <div style={card}>
                        <h3 style={h3}>Alarmas por tipo</h3>
                        {porTipo.map((t) => bar(t.evento || '(sin tipo)', Number(t.n), maxTipo, t.es_critico ? '#E23D3D' : COL[t.familia] || '#3B82C4', () => toggle(tipos, setTipos, t.evento), tipos.includes(t.evento)))}
                        {!porTipo.length && <Empty />}
                    </div>
                    {/* Por dia apilado */}
                    <div style={card}>
                        <h3 style={h3}>Alarmas por día del mes</h3>
                        <svg viewBox="0 0 460 205" width="100%" height="205">
                            {diasArr.map((d, idx) => {
                                const step = 448 / Math.max(diasArr.length, 1);
                                const x = 6 + idx * step;
                                const bw = Math.max(2, step - 1.5);
                                let y = 172; const segs = [];
                                ['Candado', 'Vehicular'].forEach((f) => {
                                    const h = (dias[d][f] / maxDia) * 148; y -= h;
                                    segs.push(<rect key={f} x={x} y={y} width={bw} height={Math.max(h, 0)} fill={COL[f]} />);
                                });
                                const sel = dia === d;
                                const showLbl = sel || idx % Math.ceil(diasArr.length / 10) === 0;
                                return (
                                    <g key={d} onClick={() => setDia(sel ? null : d)} style={{ cursor: 'pointer' }} opacity={dia && !sel ? 0.35 : 1}>
                                        {sel && <rect x={x - 1} y={18} width={bw + 2} height={156} fill="none" stroke="#0E9E97" strokeWidth="1.3" rx="2" />}
                                        {segs}
                                        {showLbl && <text x={x + bw / 2} y={188} fontSize="8.5" textAnchor="middle" fill={sel ? '#0B7E7A' : '#8494A3'} fontFamily="monospace" fontWeight={sel ? 700 : 400}>{d}</text>}
                                    </g>
                                );
                            })}
                            <text x="6" y="202" fontSize="8" fill="#8494A3">día del mes →</text>
                        </svg>
                        <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#8494A3' }}>
                            <span><span style={{ ...swatch, background: COL.Candado }} />Candado</span>
                            <span><span style={{ ...swatch, background: COL.Vehicular }} />Vehicular</span>
                        </div>
                        {!diasArr.length && <Empty />}
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                    {/* Empresas */}
                    <div style={card}>
                        <h3 style={h3}>Empresas con más alarmas</h3>
                        {porEmpresa.map((e) => bar(e.empresa, Number(e.n), maxEmp, '#0E9E97', () => toggle(empresas, setEmpresas, e.empresa), empresas.includes(e.empresa)))}
                        {!porEmpresa.length && <Empty />}
                    </div>
                    {/* Vehiculos (fila clickeable -> filtra por placa) */}
                    <div style={card}>
                        <h3 style={h3}>Vehículos con más alarmas <span style={hint}>· clic para filtrar</span></h3>
                        <DataTable value={porVehiculo} size="small" responsiveLayout="scroll" emptyMessage="Sin datos"
                            rowClassName={(r) => (placas.includes(r.placa) ? 'p-highlight' : '')}
                            onRowClick={(e) => e.data.placa && toggle(placas, setPlacas, e.data.placa)}
                            style={{ cursor: 'pointer' }}>
                            <Column field="placa" header="Placa" />
                            <Column field="empresa" header="Empresa" />
                            <Column header="Alarmas" body={(r) => num(r.n)} />
                        </DataTable>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                    {/* Rutas (clickeable -> filtra por ruta) */}
                    <div style={card}>
                        <h3 style={h3}>Rutas con más alarmas <span style={hint}>· clic para filtrar</span></h3>
                        {porRuta.map((r) => bar(r.ruta_nombre || '(sin ruta)', Number(r.n), maxRuta, '#7C56E6', () => toggle(rutas, setRutas, r.ruta_nombre), rutas.includes(r.ruta_nombre)))}
                        {!porRuta.length && <Empty msg="Sin ruta asignada en los viajes del período." />}
                    </div>
                    <div style={card}>
                        <h3 style={h3}>📡 Zonas oscuras (pérdida de señal) <span style={hint}>· clic en el sector o en # tramos para ver el detalle en el mapa</span></h3>
                        <DataTable value={zonas} size="small" responsiveLayout="scroll" emptyMessage="Sin zonas oscuras en el filtro."
                            rowClassName={(r) => (sectorSel === r.sector_nombre ? 'p-highlight' : '')}>
                            <Column header="Sector" body={(r) => (
                                <span onClick={() => verSector(r.sector_nombre, r.lat, r.lon)}
                                    style={{ color: '#0B7E7A', cursor: 'pointer', fontWeight: 600 }}
                                    title="Ver sus tramos en el mapa">📍 {nombreSector(r.sector_nombre)}
                                    <span style={{ ...hint, marginLeft: 6 }}>{r.sector_nombre}</span></span>)} />
                            <Column header="Tramos" body={(r) => (
                                <span onClick={() => verSector(r.sector_nombre, r.lat, r.lon)} style={{ color: '#0B7E7A', cursor: 'pointer', fontWeight: 700 }}>{num(r.tramos)}</span>)} />
                            <Column header="Min prom." body={(r) => num(r.min_prom)} />
                            <Column header="Con riesgo" body={(r) => num(r.con_riesgo)} />
                        </DataTable>
                    </div>
                </div>

                {/* Mapa de calor + marcadores de riesgo */}
                <div id="dashgps-mapa" style={{ ...card, marginBottom: 14 }}>
                    <h3 style={h3}>🗺️ {recorrido ? `Recorrido del vehículo ${placas[0] || ''}` : 'Mapa de calor de alarmas'}
                        <span style={hint}> · {recorrido ? '🔵 traza · A inicio / B fin · puntos = alarmas (rojo crítico)' : '🔴 riesgo ALTO · 🟠 riesgo MEDIO (parada / apertura de candado en zona oscura)'}</span>
                        {placas.length === 1 && !recorrido && <Button label="Ver recorrido" icon="pi pi-map" loading={cargandoRec} onClick={verRecorrido} className="p-button-sm p-button-text" style={{ marginLeft: 8, padding: '2px 8px' }} />}
                        {(foco || sectorSel || recorrido) && <span onClick={limpiarFoco} style={{ ...hint, color: '#E23D3D', cursor: 'pointer', marginLeft: 8 }}>✕ quitar foco</span>}</h3>
                    <MapaCalorGps puntos={mapaCalor} riesgos={riesgos} foco={foco} tramos={tramosSel} recorrido={recorrido}
                        eventos={(data && data.puntosAlarma) || []} totalEventos={k.total} onReady={() => setMapReady(true)} />

                    {/* Detalle de tramos del sector seleccionado */}
                    {sectorSel && (
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
                                Tramos sin señal en {nombreSector(sectorSel)} <span style={hint}>{sectorSel} · {(tramosSel || []).length} tramo(s)</span>
                            </div>
                            <DataTable value={tramosSel || []} size="small" responsiveLayout="scroll" paginator rows={6}
                                onRowClick={(e) => irAlMapa(e.data.latitud, e.data.longitud, `${e.data.placa || ''}`, e.data.minutos_sin_senal)}
                                style={{ cursor: 'pointer' }} emptyMessage="Sin tramos.">
                                <Column field="placa" header="Placa" />
                                <Column field="grupo" header="Grupo" />
                                <Column header="Sin señal (desde → hasta)" body={(r) => (
                                    <span>{fmtFecha(r.fecha_ini)} → {fmtFecha(finZona(r))} <span style={hint}>({num(r.minutos_sin_senal)} min)</span></span>)} />
                                <Column header="Riesgo" body={(r) => (r.riesgo_contaminacion
                                    ? <span style={{ background: r.riesgo_contaminacion === 'ALTO' ? '#FCE4E4' : '#FBF0DA', color: r.riesgo_contaminacion === 'ALTO' ? '#C22' : '#9A6A0A', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999 }}>{r.riesgo_contaminacion}</span>
                                    : <span style={hint}>—</span>)} />
                            </DataTable>
                        </div>
                    )}
                </div>

                {/* Riesgo de contaminación (detalle) */}
                <div style={{ ...card, marginBottom: 14 }}>
                    <h3 style={h3}>🔴 Riesgo de contaminación de carga
                        <span style={hint}> · zonas oscuras con parada + apertura de candado · clic en la fila para ubicarlo</span></h3>
                    <DataTable value={riesgos} size="small" responsiveLayout="scroll" paginator rows={8}
                        onRowClick={(e) => irAlMapa(e.data.latitud, e.data.longitud, `${e.data.placa || ''} · ${nombreSector(e.data.sector_nombre)}`, e.data.minutos_sin_senal)}
                        style={{ cursor: 'pointer' }}
                        emptyMessage="Sin riesgos de contaminación detectados en el filtro.">
                        <Column header="Riesgo" body={(r) => (
                            <span style={{ background: r.riesgo_contaminacion === 'ALTO' ? '#FCE4E4' : '#FBF0DA', color: r.riesgo_contaminacion === 'ALTO' ? '#C22' : '#9A6A0A', fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 999 }}>{r.riesgo_contaminacion}</span>)} />
                        <Column field="placa" header="Placa" />
                        <Column field="grupo" header="Grupo" />
                        <Column header="Sector" body={(r) => (
                            <span style={{ color: '#0B7E7A', fontWeight: 600 }}>📍 {nombreSector(r.sector_nombre)}
                                <span style={{ ...hint, marginLeft: 6 }}>{r.sector_nombre}</span></span>)} />
                        <Column header="Sin señal (desde → hasta)" body={(r) => (
                            <span>{fmtFecha(r.fecha_ini)} → {fmtFecha(finZona(r))} <span style={hint}>({num(r.minutos_sin_senal)} min)</span></span>)} />
                    </DataTable>
                </div>
            </>}
        </div>
    );
};

const lbl = { fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.05em', color: '#8494A3', fontWeight: 600, marginBottom: 4 };
const h3 = { fontSize: 14, margin: '0 0 12px', fontWeight: 650 };
const hint = { fontSize: 11, fontWeight: 400, color: '#8494A3' };
const swatch = { display: 'inline-block', width: 9, height: 9, borderRadius: 3, marginRight: 6 };
const Empty = ({ msg }) => <div style={{ color: '#8494A3', fontSize: 12.5, padding: 16, textAlign: 'center' }}>{msg || 'Sin datos para el filtro.'}</div>;

export default DashboardAlarmasGps;
